import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { downloadExcel } from "../../finance/shared/excelService";
import { useToast } from "../../../shared/components/SharedUI";
import RowActionsMenu from "../../../shared/components/RowActionsMenu";
import { useLang } from "../../../shared/i18n/useLang";
import { usePermisos } from "../../../shared/utils/permisos";
import { overlayClose } from "../../../shared/utils/overlayClose";
import {
  duracion, hora, listarRegistros, marcarSalida, resumenRegistros, texto as textoDe, textoMotivo,
} from "../services/seguridadService";
import fs from "../../finance/shared/fichas.module.css";
import s from "../seguridadAdmin.module.css";

const TAMANOS = [10, 25, 50, 100];

function hoyISO(dias = 0) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fecha(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

/**
 * Bitácora de seguridad del condominio (administración): todos los ingresos y
 * salidas registrados por los guardas, con resumen del día, filtros, orden por
 * columna y exportación a Excel.
 */
export default function SeguridadBitacora() {
  const { showToast, toastEl } = useToast();
  const L = useLang();
  const puede = usePermisos();
  const [filtros, setFiltros] = useState({ desde: hoyISO(-7), hasta: hoyISO(), q: "", tipo: "", adentro: false });
  const [orden, setOrden] = useState("-entrada");
  const [page, setPage] = useState(1);
  const [tamano, setTamano] = useState(25);
  const [datos, setDatos] = useState({ results: [], count: 0 });
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [recarga, setRecarga] = useState(0);

  const params = useCallback(() => ({
    desde: filtros.desde, hasta: filtros.hasta, q: filtros.q, tipo: filtros.tipo,
    adentro: filtros.adentro ? 1 : "", orden,
  }), [filtros, orden]);

  useEffect(() => {
    let cancel = false;
    setCargando(true);
    listarRegistros({ ...params(), page, page_size: tamano })
      .then((d) => !cancel && setDatos(d.results ? d : { results: d, count: d.length }))
      .catch((e) => !cancel && showToast(e.message, false))
      .finally(() => !cancel && setCargando(false));
    return () => { cancel = true; };
    // showToast cambia en cada render; no hace falta como dependencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, page, tamano, recarga]);

  useEffect(() => {
    resumenRegistros().then(setResumen).catch(() => setResumen(null));
  }, [recarga]);

  function cambiar(campo, valor) {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPage(1);
  }

  function ordenarPor(campo) {
    setOrden((o) => (o === `-${campo}` ? campo : `-${campo}`));
    setPage(1);
  }

  async function exportar() {
    const u = new URLSearchParams(Object.entries(params()).filter(([, v]) => v !== "" && v != null));
    try {
      await downloadExcel(`/api/seguridad/registros/exportar-excel/?${u}`, "bitacora_seguridad.xlsx");
    } catch (e) {
      showToast(e.message, false);
    }
  }

  async function salida(r) {
    try {
      await marcarSalida(r.id);
      showToast(L(`Salida registrada: ${r.nombre}`, `Exit registered: ${r.nombre}`));
      setRecarga((x) => x + 1);
    } catch (e) {
      showToast(e.message, false);
    }
  }

  const paginas = Math.max(1, Math.ceil(datos.count / tamano));
  const variacion = resumen && resumen.ayer > 0 ? Math.round(((resumen.hoy - resumen.ayer) / resumen.ayer) * 100) : null;
  const pct = (n) => (resumen && resumen.hoy > 0 ? Math.round((n / resumen.hoy) * 100) : 0);

  // Encabezado ordenable
  const Th = ({ campo, children }) => {
    if (!campo) return <th>{children}</th>;
    const activo = orden.replace("-", "") === campo;
    const icono = !activo ? "fa-sort" : orden.startsWith("-") ? "fa-sort-down" : "fa-sort-up";
    return (
      <th className={`${s.ordenable} ${activo ? s.ordenActivo : ""}`} onClick={() => ordenarPor(campo)}>
        {children}<i className={`fa-solid ${icono} ${s.ordenIcono}`} />
      </th>
    );
  };

  return (
    <div className={s.page}>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/" style={{ color: "inherit" }}>{L("Inicio", "Home")}</Link> /{" "}
        <strong style={{ color: "var(--text-primary)" }}>{L("Seguridad", "Security")}</strong>
      </span>
      <div className={s.header}>
        <div>
          <h2 className="mt-2 mb-1 p-0 title">{L("Bitácora de seguridad", "Security log")}</h2>
          <p>{L("Ingresos y salidas registrados por los guardas", "Entries and exits registered by the guards")}</p>
        </div>
        <div className={s.acciones}>
          {puede("seguridad_garita") && (
            <Link to="/garita" className={s.btn}>
              <i className="fa-solid fa-shield-halved" /> {L("Abrir app de Seguridad", "Open Security app")}
            </Link>
          )}
          <button type="button" className={`${s.btn} ${s.btnMarca}`} onClick={exportar}>
            <i className="fa-solid fa-file-excel" /> {L("Exportar Excel", "Export Excel")}
          </button>
        </div>
      </div>

      {/* ── Filtros ───────────────────────────────────────────────────── */}
      <div className={s.filtros}>
        <div className={s.campo} style={{ flex: "0 1 170px" }}>
          <label htmlFor="bit-desde">{L("Desde", "From")}</label>
          <input id="bit-desde" type="date" className={s.input} value={filtros.desde} onChange={(e) => cambiar("desde", e.target.value)} />
        </div>
        <div className={s.campo} style={{ flex: "0 1 170px" }}>
          <label htmlFor="bit-hasta">{L("Hasta", "To")}</label>
          <input id="bit-hasta" type="date" className={s.input} value={filtros.hasta} onChange={(e) => cambiar("hasta", e.target.value)} />
        </div>
        <div className={s.campo} style={{ flex: "1 1 260px" }}>
          <label htmlFor="bit-q">{L("Buscar", "Search")}</label>
          <div className={s.conIcono}>
            <i className="fa-solid fa-magnifying-glass" />
            <input id="bit-q" className={s.input} value={filtros.q} onChange={(e) => cambiar("q", e.target.value)}
              placeholder={L("Nombre, cédula, placa, casa o guarda", "Name, ID, plate, unit or guard")} />
          </div>
        </div>
        <div className={s.campo} style={{ flex: "0 1 170px" }}>
          <label htmlFor="bit-tipo">{L("Tipo", "Type")}</label>
          <select id="bit-tipo" className={s.input} value={filtros.tipo} onChange={(e) => cambiar("tipo", e.target.value)}>
            <option value="">{L("Todos", "All")}</option>
            <option value="VEHICULAR">{textoDe("VEHICULAR")}</option>
            <option value="PEATONAL">{textoDe("PEATONAL")}</option>
          </select>
        </div>
        <label className={s.switch}>
          <input type="checkbox" checked={filtros.adentro} onChange={(e) => cambiar("adentro", e.target.checked)} />
          <span className={s.switchTrack} />
          {L("Solo los que siguen adentro", "Only those still inside")}
        </label>
      </div>

      {/* ── Resumen de hoy ────────────────────────────────────────────── */}
      <div className={s.stats}>
        <div className={`${s.stat} ${fs.orange}`}>
          <div className={s.statIcon}><i className="fa-solid fa-clipboard-list" /></div>
          <div>
            <div className={s.statLabel}>{L("Registros hoy", "Entries today")}</div>
            <div className={s.statFila}>
              <span className={s.statValor}>{resumen?.hoy ?? "-"}</span>
              {variacion !== null && (
                <span className={`${s.chip} ${fs[variacion >= 0 ? "green" : "red"]}`}
                  title={L(`Ayer: ${resumen.ayer}`, `Yesterday: ${resumen.ayer}`)}>
                  <i className={`fa-solid ${variacion >= 0 ? "fa-arrow-up" : "fa-arrow-down"}`} />
                  {variacion >= 0 ? "+" : ""}{variacion}%
                </span>
              )}
              {variacion !== null && <span className={s.statNota}>{L("vs. ayer", "vs. yesterday")}</span>}
            </div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.blue}`}>
          <div className={s.statIcon}><i className="fa-solid fa-car-side" /></div>
          <div>
            <div className={s.statLabel}>{L("Vehiculares", "Vehicles")}</div>
            <div className={s.statFila}>
              <span className={s.statValor}>{resumen?.vehiculares ?? "-"}</span>
              {resumen && <span className={s.chip}>{pct(resumen.vehiculares)}%</span>}
              <span className={s.statNota}>{L("de hoy", "of today")}</span>
            </div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.green}`}>
          <div className={s.statIcon}><i className="fa-solid fa-person-walking" /></div>
          <div>
            <div className={s.statLabel}>{L("Peatonales", "On foot")}</div>
            <div className={s.statFila}>
              <span className={s.statValor}>{resumen?.peatonales ?? "-"}</span>
              {resumen && <span className={s.chip}>{pct(resumen.peatonales)}%</span>}
              <span className={s.statNota}>{L("de hoy", "of today")}</span>
            </div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.purple}`}>
          <div className={s.statIcon}><i className="fa-solid fa-people-group" /></div>
          <div>
            <div className={s.statLabel}>{L("Dentro actualmente", "Inside now")}</div>
            <div className={s.statFila}>
              <span className={s.statValor}>{resumen?.adentro ?? "-"}</span>
              <span className={`${s.chip} ${fs.green}`}><span className={s.punto} />{L("En el condominio", "In the condominium")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabla ─────────────────────────────────────────────────────── */}
      <div className={s.tabla}>
        <div className={s.scroll}>
          <table>
            <thead>
              <tr>
                <Th campo="entrada">{L("Entrada", "In")}</Th>
                <Th campo="salida">{L("Salida", "Out")}</Th>
                <Th>{L("Tipo", "Type")}</Th>
                <Th campo="nombre">{L("Persona", "Person")}</Th>
                <Th campo="placa">{L("Placa", "Plate")}</Th>
                <Th campo="filial_nombre">{L("Casa", "Unit")}</Th>
                <Th>{L("Visita a", "Visiting")}</Th>
                <Th campo="motivo">{L("Motivo", "Reason")}</Th>
                <Th>{L("Registró", "Logged by")}</Th>
                <Th campo="estado">{L("Estado", "Status")}</Th>
                <th aria-label={L("Acciones", "Actions")} />
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={11} className={s.vacio}><i className="fa-solid fa-circle-notch fa-spin" /> {L("Cargando…", "Loading…")}</td></tr>
              ) : datos.results.length === 0 ? (
                <tr><td colSpan={11} className={s.vacio}>{L("No hay registros con estos filtros.", "No records match these filters.")}</td></tr>
              ) : datos.results.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className={s.fecha}>
                      <i className="fa-regular fa-calendar" />
                      <div>{fecha(r.entrada)}<div className={s.fechaSub}>{hora(r.entrada)}</div></div>
                    </div>
                  </td>
                  <td>
                    {r.salida ? (
                      <div className={s.fecha}>
                        <i className="fa-regular fa-calendar" />
                        <div>
                          {fecha(r.salida)}<div className={s.fechaSub}>{hora(r.salida)}</div>
                          <div className={s.duracion}><i className="fa-regular fa-clock" />{duracion(r.entrada, r.salida)}</div>
                        </div>
                      </div>
                    ) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                  </td>
                  <td>
                    <span className={`${s.pastilla} ${fs[r.tipo === "VEHICULAR" ? "blue" : "purple"]}`}>
                      <i className={`fa-solid ${r.tipo === "VEHICULAR" ? "fa-car-side" : "fa-person-walking"}`} /> {textoDe(r.tipo)}
                    </span>
                  </td>
                  <td className={s.persona}>
                    <strong>{r.nombre}</strong>
                    <div className={s.personaSub}>{r.identificacion} - {textoDe(r.categoria)}</div>
                    {r.autorizado_por_nombre && (
                      <div className={`${s.personaExtra} ${s.personaAut}`}>
                        <i className={`fa-solid ${r.autorizacion_origen === "GARITA" ? "fa-shield-halved" : "fa-user-check"}`} />{" "}
                        {r.autorizacion_origen === "GARITA"
                          ? L(`Autorizado por el guarda ${r.autorizado_por_nombre}`,
                              `Authorized by the guard ${r.autorizado_por_nombre}`)
                          : L(`Autorizado por ${r.autorizado_por_nombre}`,
                              `Authorized by ${r.autorizado_por_nombre}`)}
                      </div>
                    )}
                    {r.notas && (
                      <div className={s.personaExtra} title={r.notas}>
                        <i className="fa-solid fa-note-sticky" />
                        {r.notas.length > 60 ? `${r.notas.slice(0, 60)}…` : r.notas}
                      </div>
                    )}
                  </td>
                  <td>{r.placa ? <span className={s.placa}>{r.placa}</span> : "-"}</td>
                  <td><span className={s.fuerte}>{r.filial_nombre || "-"}</span></td>
                  <td>{r.persona_visitada || "-"}</td>
                  <td>{textoMotivo(r.motivo)}</td>
                  <td>
                    <span style={{ color: "var(--text-primary)" }}>{r.registrado_por_nombre || "-"}</span>
                    {r.salida_por_nombre && r.salida_por_nombre !== r.registrado_por_nombre && (
                      <div className={s.personaSub}>{L("Salida:", "Exit:")} {r.salida_por_nombre}</div>
                    )}
                  </td>
                  <td>
                    {r.salida ? (
                      <span className={`${s.pastilla} ${fs.gray}`}><i className="fa-solid fa-circle-check" /> {L("Salida registrada", "Exit registered")}</span>
                    ) : (
                      <span className={`${s.pastilla} ${fs.green}`}><span className={s.punto} /> {L("Adentro", "Inside")}</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RowActionsMenu items={[
                      { icon: "fa-eye", label: L("Ver detalle", "View details"), onClick: () => setDetalle(r) },
                      !r.salida && puede("seguridad_garita") && {
                        icon: "fa-right-from-bracket", label: L("Marcar salida", "Mark exit"), color: "#f97316", onClick: () => salida(r),
                      },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={s.pie}>
          <span>
            {datos.count === 0 ? L("Sin registros", "No records")
              : L(`Mostrando ${datos.results.length} de ${datos.count} registros`, `Showing ${datos.results.length} of ${datos.count} records`)}
          </span>
          <div className={s.pagina}>
            <select value={tamano} onChange={(e) => { setTamano(Number(e.target.value)); setPage(1); }}
              aria-label={L("Registros por página", "Records per page")}>
              {TAMANOS.map((n) => <option key={n} value={n}>{L(`${n} por página`, `${n} per page`)}</option>)}
            </select>
            <button type="button" className={s.pagBtn} disabled={page <= 1} onClick={() => setPage(1)} title={L("Primera", "First")}>
              <i className="fa-solid fa-angles-left" />
            </button>
            <button type="button" className={s.pagBtn} disabled={page <= 1} onClick={() => setPage(page - 1)} title={L("Anterior", "Previous")}>
              <i className="fa-solid fa-angle-left" />
            </button>
            <span className={`${s.pagBtn} ${s.actual}`}>{page}</span>
            <span style={{ color: "var(--text-muted)" }}>{L(`de ${paginas}`, `of ${paginas}`)}</span>
            <button type="button" className={s.pagBtn} disabled={page >= paginas} onClick={() => setPage(page + 1)} title={L("Siguiente", "Next")}>
              <i className="fa-solid fa-angle-right" />
            </button>
            <button type="button" className={s.pagBtn} disabled={page >= paginas} onClick={() => setPage(paginas)} title={L("Última", "Last")}>
              <i className="fa-solid fa-angles-right" />
            </button>
          </div>
        </div>
      </div>

      {detalle && <DetalleModal r={detalle} onClose={() => setDetalle(null)} />}
      {toastEl}
    </div>
  );
}

function DetalleModal({ r, onClose }) {
  const L = useLang();
  const filas = [
    [L("Entrada", "In"), `${fecha(r.entrada)} ${hora(r.entrada)}`],
    [L("Salida", "Out"), r.salida ? `${fecha(r.salida)} ${hora(r.salida)} (${duracion(r.entrada, r.salida)})` : L("Sigue adentro", "Still inside")],
    [L("Tipo", "Type"), textoDe(r.tipo)],
    [L("Persona", "Person"), r.nombre],
    [L("Identificación", "ID"), r.identificacion],
    [L("Categoría", "Category"), textoDe(r.categoria)],
    [L("Placa", "Plate"), r.placa || "-"],
    [L("Casa", "Unit"), r.filial_nombre || "-"],
    [L("Visita a", "Visiting"), r.persona_visitada || "-"],
    [L("Motivo", "Reason"), textoMotivo(r.motivo)],
    [L("Autorizado por", "Authorized by"), r.autorizado_por_nombre || "-"],
    [L("Tipo de autorización", "Authorization"), r.autorizacion_origen === "GARITA"
      ? L("El guarda (solo ese día)", "The guard (that day only)")
      : r.autorizacion_origen === "RESIDENTE"
        ? L("El residente desde el portal", "The resident from the portal")
        : "-"],
    [L("Registró", "Logged by"), r.registrado_por_nombre || "-"],
    [L("Marcó la salida", "Exit by"), r.salida_por_nombre || "-"],
    [L("Notas", "Notes"), r.notas || "-"],
  ];
  return (
    <div className={s.overlay} {...overlayClose(onClose)}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3><i className="fa-solid fa-clipboard-list" style={{ color: "var(--brand)", marginRight: 8 }} />{L("Detalle del registro", "Record details")}</h3>
          <button type="button" className={s.iconBtn} onClick={onClose} aria-label={L("Cerrar", "Close")}><i className="fa-solid fa-xmark" /></button>
        </div>
        <dl className={s.detalle}>
          {filas.map(([k, v]) => <div key={k} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>)}
        </dl>
      </div>
    </div>
  );
}
